import * as React from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';

import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';

import type { AssetMediaFormat } from 'types';

applyTranslations(require('./drag-and-drop.i18n.json'));

type Props = {
  onDrop: (a: Array<File>) => void,
  children: React.ReactNode,
  allowedFileTypes?: Array<AssetMediaFormat>,
  renderOverlay?: () => React.ReactNode
};

type State = {
  isDragAndDropping: boolean
};

class DragAndDrop extends React.PureComponent<Props, State> {
  state = {
    isDragAndDropping: false
  };

  handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      isDragAndDropping: true
    });
  };

  handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      isDragAndDropping: false
    });
  };

  handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const { onDrop, allowedFileTypes } = this.props;
    const files = event.dataTransfer && event.dataTransfer.files;

    event.preventDefault();
    event.stopPropagation();

    this.setState({
      isDragAndDropping: false
    });

    try {
      let filteredFiles;
      if (isNil(allowedFileTypes)) {
        // files is type of FileList by default
        filteredFiles = [...files];
      } else {
        filteredFiles = [...files].filter(file => allowedFileTypes.some(type => file.type.indexOf(type) >= 0));
      }
      onDrop(filteredFiles);
    } catch (error) {
      logError(error, files);
    }
  };

  render(): React.ReactNode {
    const { children, renderOverlay } = this.props;
    const { isDragAndDropping } = this.state;

    const dropzoneClasses = classnames({
      'drag-and-drop__dropzone': true,
      'drag-and-drop__dropzone--hovering': isDragAndDropping
    });

    const overlayWrapperClasses = classnames({
      'drag-and-drop__overlay-wrapper': true,
      'drag-and-drop__overlay-wrapper--hovering': isDragAndDropping
    });

    return (
      <div
        className="drag-and-drop"
        onDragEnter={this.handleDragEnter}
        onDrop={this.handleDrop}
        onDragOver={this.handleDragOver}
      >
        <div className={dropzoneClasses}>
          {children}
        </div>
        {
          isDragAndDropping && (
            <div
              className={overlayWrapperClasses}
              onDragLeave={this.handleDragLeave}
            >
              {
                renderOverlay ? (
                  renderOverlay()
                ) : (
                  <div className="drag-and-drop__default-overlay">
                    <span>{__t('drag_and_drop.drop_here_label')}</span>
                  </div>
                )
              }
            </div>
          )
        }
      </div>
    );
  }
}

export default DragAndDrop;
